import { ReactNode, useEffect, useRef } from 'react';
import { MobileNav } from './MobileNav';
import { DesktopSidebar } from './DesktopSidebar';
import { TopBar } from './TopBar';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ToastAction } from '@/components/ui/toast';
import { useNavigate } from 'react-router-dom';

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const { companyId, isDemoAccount } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const notifiedVisitIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel(`visits-known-customer-entered-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'visits',
          filter: `company_id=eq.${companyId}`,
        },
        async (payload) => {
          const newVisit = (payload.new || {}) as {
            id?: string;
            customer_id?: string | null;
            company_id?: string | null;
          };

          const visitId = newVisit.id;
          const customerId = newVisit.customer_id;
          if (!visitId || !customerId) return;

          if (notifiedVisitIdsRef.current.has(visitId)) return;
          notifiedVisitIdsRef.current.add(visitId);
          if (notifiedVisitIdsRef.current.size > 500) {
            const firstId = notifiedVisitIdsRef.current.values().next().value as string | undefined;
            if (firstId) notifiedVisitIdsRef.current.delete(firstId);
          }

          let customerName = 'Bir müşteri';
          try {
            const { data: customer } = await supabase
              .from('customers')
              .select('first_name, last_name')
              .eq('id', customerId)
              .maybeSingle();

            if (customer) {
              const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
              if (fullName) customerName = fullName;
            }
          } catch (error) {
            console.warn('Could not load customer name for visit entry toast:', error);
          }

          toast({
            title: 'Müşteri Girişi',
            description: `${customerName} mağazaya giriş yaptı.`,
            action: (
              <ToastAction altText="Müşteri detayını aç" onClick={() => navigate(`/customers/${customerId}`)}>
                Detayı Aç
              </ToastAction>
            ),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, navigate, toast]);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full">
        <DesktopSidebar />
        
        <div className="flex-1 flex flex-col transition-all duration-300">
          <TopBar />
          
          {isDemoAccount && (
            <div className="mx-3 mt-2 sm:mx-4 rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-2 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span><strong>Demo Modu</strong> — Veriler sadece goruntuleme amacidir. Yaptiginiz degisiklikler kaydedilmez.</span>
            </div>
          )}
          <main className="flex-1 p-3 pb-20 sm:p-4 md:pb-4">
            {children}
          </main>
        </div>
        
        <MobileNav />
      </div>
    </SidebarProvider>
  );
};